"use client";

import { useState, useId, useEffect } from "react";
import OtpInput from "react-otp-input";
import type { Session } from "next-auth";
import { useRouter } from "next/navigation";

import { OnboardingStages } from "@/types";
import { errorIntoString } from "@/lib/utils";
import { useOtpValidateMutation } from "@/features/auth/authApiInjection";
import { getSafeRedirectUrl } from "@/lib/utils/returnUrl";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";

type Props = {
	session?: Session | null;
	authToken?: string;
	updateSession?: (x: unknown) => Promise<Session | null>;
	returnUrlOverride?: string;
	onVerified?: () => void | Promise<void>;
};

const OTP_STORAGE_KEY = "signup_otp_value";

export default function VerificationForm({ session, authToken, updateSession, returnUrlOverride, onVerified }: Props) {
	const router = useRouter();
	const { toast } = useToast();
	const otpInputId = useId();

	// SECURITY: Only OTP value is stored in sessionStorage; never store auth tokens or passwords here.
	// Restore OTP from sessionStorage if available (persists across tab switches)
	const [otp, setOtp] = useState(() => {
		if (typeof window !== "undefined") {
			return sessionStorage.getItem(OTP_STORAGE_KEY) || "";
		}
		return "";
	});
	const [isVerifying, setIsVerifying] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [otpValidation, { isLoading }] = useOtpValidateMutation();

	// Persist OTP to sessionStorage whenever it changes
	useEffect(() => {
		if (typeof window !== "undefined") {
			if (otp) {
				sessionStorage.setItem(OTP_STORAGE_KEY, otp);
			} else {
				sessionStorage.removeItem(OTP_STORAGE_KEY);
			}
		}
	}, [otp]);

	// Clear OTP from storage on successful verification
	const clearStoredOtp = () => {
		if (typeof window !== "undefined") {
			sessionStorage.removeItem(OTP_STORAGE_KEY);
		}
	};

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		if (!session && !authToken) throw new Error("verification session not found");

		const normalizedOtp = otp.replace(/\D/g, "").slice(0, 4);
		if (normalizedOtp.length !== 4) {
			return toast({
				variant: "destructive",
				title: "Code Verification Error",
				description: "Please enter the 4-digit code from your email.",
			});
		}

		setIsVerifying(true);

		try {
			if (authToken) {
				const backendUrl = (process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com").replace(
					/\/$/,
					"",
				);
				const response = await fetch(`${backendUrl}/auth/otp-validate`, {
					method: "POST",
					headers: {
						Authorization: `Bearer ${authToken}`,
						"Content-Type": "application/json",
						"X-Requested-With": "XMLHttpRequest",
					},
					body: JSON.stringify({ code: normalizedOtp }),
				});

				if (!response.ok) {
					let description = "The verification code is invalid or has expired.";
					try {
						const data = await response.json();
						if (typeof data?.message === "string") description = data.message;
						else if (Array.isArray(data?.message) && typeof data.message[0] === "string") {
							description = data.message[0];
						}
					} catch {
						// ignore parse errors
					}
					setIsVerifying(false);
					return toast({
						variant: "destructive",
						title: "Code Verification Error",
						description,
					});
				}
			} else {
				const result = await otpValidation(normalizedOtp);
				if ("error" in result) {
					setIsVerifying(false);
					const ex = errorIntoString(result.error);
					return toast({
						variant: "destructive",
						title: "Code Verification Error",
						description: ex,
					});
				}
			}
		} catch {
			setIsVerifying(false);
			return toast({
				variant: "destructive",
				title: "Code Verification Error",
				description: "Could not verify your code. Check your connection and try again.",
			});
		}

		// Mark as verified immediately to hide the form
		setIsVerified(true);

		// Clear stored OTP on success (but keep OTP in state to prevent empty boxes flash)
		clearStoredOtp();

		if (updateSession) {
			await updateSession({
				user: {
					onboardingStages: OnboardingStages.ACC_VERIFIED,
				},
			});
		}

		toast({ title: "Code Verification Success" });

		if (onVerified) {
			try {
				await onVerified();
			} catch (error) {
				setIsVerified(false);
				setIsVerifying(false);
				const ex = errorIntoString(error);
				toast({
					variant: "destructive",
					title: "Sign-in Error",
					description: ex || "Account verified, but sign-in could not be completed.",
				});
			}
			return;
		}

		const searchParams = new URLSearchParams(window.location.search);
		const returnUrl = returnUrlOverride ?? searchParams.get("returnUrl");
		const safeUrl = getSafeRedirectUrl(returnUrl);

		if (safeUrl.startsWith("http")) {
			window.location.href = safeUrl;
			return;
		}

		router.replace(safeUrl);
		router.refresh();
	}

	// Show loading state immediately after successful verification
	if (isVerified || isVerifying) {
		return (
			<div className="flex flex-col items-center gap-4">
				<LoadingSpinner fullScreen={false} layout="inline" />
				<p className="text-sm text-muted-foreground">
					{isVerified ? "Signing you in..." : "Verifying your code..."}
				</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="grid grid-cols-4 justify-items-center gap-8">
			<OtpInput
				value={otp}
				onChange={setOtp}
				numInputs={4}
				containerStyle={{ display: "contents" }}
				renderInput={(props, index) => (
					<Input
						{...props}
						id={`${otpInputId}-${index}`}
						name={`otp-${index}`}
						aria-label={`Verification code digit ${index + 1}`}
						className="h-12 w-11 border border-accent-foreground/50"
						disabled={isLoading || isVerifying}
					/>
				)}
				inputStyle={{ width: 48 }}
				inputType="number"
			/>

			<Button type="submit" size="lg" className="col-span-full" disabled={isLoading || isVerifying}>
				{isLoading || isVerifying ? <LoadingSpinner fullScreen={false} layout="inline" /> : "Submit"}
			</Button>
		</form>
	);
}
